/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Cart, CartLine, CommerceProperty } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

/**
 * What the store says about a cart and its lines, and the only place this channel reads it from.
 *
 * Every fact here is the store's to establish. Nothing is derived from the shape of an id, from a line's
 * age, or from what this browser happens to remember, because none of those can be enforced - a control
 * this channel disables is a courtesy, and the store refusing the write is the guard.
 *
 * Keys and codes are the store's. Keeping them behind this module means a rename costs one file.
 */

/** The store already holds an order for this cart. */
const CART_COMMITTED_TO_ORDER = 'ITSCartCommittedToOrder';

/** This line is on the store's copy of the order. */
const LINE_COMMITTED_TO_ORDER = 'ITSCommittedToOrder';

/** The kitchen has been told about this line. */
const LINE_SENT_TO_KITCHEN = 'ITSSentToKitchen';

/** The line's menu is still on offer. */
const LINE_MENU_AVAILABLE = 'ITSLineMenuAvailable';

/** The till voided this line. It is kept and marked rather than dropped, so the guest is not left guessing. */
const LINE_VOIDED_AT_THE_TILL = 'ITSVoidedAtTheTill';

/** The store's decision on whether the line's quantity or content may be changed. */
const LINE_CAN_MODIFY = 'ITSLineCanModify';

/** The store's decision on whether the line may be taken off the order. */
const LINE_CAN_REMOVE = 'ITSLineCanRemove';

/** The line's price was keyed in or overridden at the store, so the store keeps it and this channel may not reprice it. */
const LINE_PRICE_SET_AT_STORE = 'ITSPriceSetAtStore';

/** The price the store set on a line it priced itself, carried across the pull. */
const LINE_STORE_PRICE = 'ITS_StorePrice';

/** The store found the line's modifier selections invalid, and the till cannot hold the order while it stands. */
const LINE_MODIFIERS_INVALID = 'ITSModsValidationFailed';

/** The menu this channel took the line's menu type from; sent on, it stops the store taking the type from its own menu. */
const LINE_MENU_TYPE_TAKEN_FROM = 'LAC_MenuItemTypeMenuId';

/** Whether the line is kept from the kitchen, which the store works out from the line's menu type. */
const LINE_KITCHEN_BLOCKED = 'ITS_KitchenBlocked';

/**
 * A fact the store has not spoken to is unknown, and unknown is never acted on. Only a false the store
 * actually wrote means no: it says the fact was established and the answer was no, where absence says the
 * question was never reached.
 */
export type StoreFact = 'yes' | 'no' | 'unknown';

export interface ILineOrderState {
    /** The store holds this line as part of the order. */
    committed: StoreFact;

    /** The kitchen has been told about this line. */
    sentToKitchen: StoreFact;

    /** The line's menu is still on offer. */
    menuAvailable: StoreFact;

    /** The store set this line's price itself, at the till or through another channel. */
    priceSetAtStore: StoreFact;

    /**
     * Whether the store will accept a change to this line's quantity or content, as the store itself
     * decided it. Not worked out here from the facts above: the rule is the store's, it is enforced there,
     * and a second copy of it in this channel would eventually disagree with the one that binds.
     */
    canModify: StoreFact;

    /** Whether the store will accept this line being taken off the order, as the store itself decided it. */
    canRemove: StoreFact;

    /** The till voided this line. Kept and marked, because a line that vanishes is worse than one explained. */
    voidedAtTheTill: StoreFact;
}

/**
 * Read as a boolean only. The same key has historically carried a string from this channel's own writer,
 * and a value of a shape this channel does not expect is unestablished rather than no.
 */
const readStoreFact = (properties: CommerceProperty[] | undefined, key: string): StoreFact => {
    const property = properties?.find(candidate => candidate.Key === key);
    const value = property?.Value?.BooleanValue;

    if (value === true) {
        return 'yes';
    }

    if (value === false) {
        return 'no';
    }

    return 'unknown';
};

/** Whether the store already holds an order for this cart. */
export const readCartCommitted = (cart: Cart | undefined): StoreFact => readStoreFact(cart?.ExtensionProperties, CART_COMMITTED_TO_ORDER);

/** What the store says about a single line. */
export const readLineOrderState = (line: CartLine | undefined): ILineOrderState => ({
    committed: readStoreFact(line?.ExtensionProperties, LINE_COMMITTED_TO_ORDER),
    sentToKitchen: readStoreFact(line?.ExtensionProperties, LINE_SENT_TO_KITCHEN),
    menuAvailable: readStoreFact(line?.ExtensionProperties, LINE_MENU_AVAILABLE),
    priceSetAtStore: readStoreFact(line?.ExtensionProperties, LINE_PRICE_SET_AT_STORE),
    canModify: readStoreFact(line?.ExtensionProperties, LINE_CAN_MODIFY),
    canRemove: readStoreFact(line?.ExtensionProperties, LINE_CAN_REMOVE),
    voidedAtTheTill: readStoreFact(line?.ExtensionProperties, LINE_VOIDED_AT_THE_TILL)
});

/**
 * The price the store set on a line it priced itself, or undefined for a line priced by this channel.
 * @param line - The line in question.
 * @returns The store's unit price for the line.
 */
export const readStoreSetPrice = (line: CartLine | undefined): number | undefined => {
    if (readStoreFact(line?.ExtensionProperties, LINE_PRICE_SET_AT_STORE) !== 'yes') {
        return undefined;
    }

    const price = line?.ExtensionProperties?.find(property => property.Key === LINE_STORE_PRICE)?.Value?.DecimalValue;

    return typeof price === 'number' ? price : undefined;
};

/**
 * Whether the store holds this line, answered for a line it may never have seen.
 *
 * The store stamps every line it wrote when it hands its copy over, and stamps nothing on a line added here
 * since - it does not sit on the path this channel adds lines through. So on a cart the store has spoken to,
 * a line carrying no mark is one the store did not write, which is a line the guest has added and not yet
 * sent. Where the store has said nothing about the cart either, nothing is established and it stays unknown.
 *
 * This is the one inference drawn from an absence anywhere here, and it is narrow: it reads absence as
 * meaning something only where the cart itself establishes that the stamping ran. It decides nothing about
 * what may be done with the line.
 * @param cart - The cart being drawn.
 * @param line - The line in question.
 * @returns Whether the store holds the line.
 */
export const readLineCommitment = (cart: Cart | undefined, line: CartLine | undefined): StoreFact => {
    const marked = readLineOrderState(line).committed;
    if (marked !== 'unknown') {
        return marked;
    }

    return readCartCommitted(cart) === 'yes' ? 'no' : 'unknown';
};

/** The lines the store holds that the venue must correct before the till can hold the order. */
export const findLinesTheVenueMustCorrect = (cart: Cart | undefined): CartLine[] =>
    (cart?.CartLines ?? []).filter(
        line =>
            readStoreFact(line.ExtensionProperties, LINE_MODIFIERS_INVALID) === 'yes' &&
            readLineCommitment(cart, line) === 'yes' &&
            readLineOrderState(line).voidedAtTheTill !== 'yes'
    );

/**
 * Whether the guest may still act on a line.
 *
 * The store decides this and says so. It is produced by the same rule that refuses a change when one
 * arrives, so what the guest is offered and what the store will accept cannot disagree - which is the whole
 * reason the decision crosses the wire rather than the rule being held here as well.
 *
 * A decision the store has not given is not one this channel invents. Nothing established means allowed:
 * the store refuses what it will not accept, so a control left enabled costs a refusal the guest can read,
 * where a control disabled on a guess costs them an action they should have had.
 */
const storeWouldAllow = (decision: StoreFact): boolean => decision !== 'no';

/** Whether the guest may change this line's quantity or content. */
export const mayChangeLine = (state: ILineOrderState): boolean => storeWouldAllow(state.canModify);

/** Whether the guest may take this line off the order. */
export const mayRemoveLine = (state: ILineOrderState): boolean => storeWouldAllow(state.canRemove);

/**
 * Why a line cannot be changed, for telling the guest.
 *
 * Only ever answered for a line the store has actually restricted. The facts behind it can be true of a line
 * that is not restricted at all - a line the store does not yet hold stays the guest's own even where the
 * kitchen flag or the menu flag says otherwise - so the store's decision gates the reason, and the reason only
 * names which of the two it was.
 * @param state - What the store said about the line.
 * @returns The reason to show, or undefined where nothing is restricted or nothing was established.
 */
export const readLineChangeReason = (state: ILineOrderState): LineChangeRefusal | undefined => {
    if (state.canModify !== 'no' && state.canRemove !== 'no') {
        return undefined;
    }

    if (state.sentToKitchen === 'yes') {
        return 'sent-to-kitchen';
    }

    if (state.priceSetAtStore === 'yes') {
        return 'price-set-at-store';
    }

    return state.menuAvailable === 'no' ? 'menu-unavailable' : undefined;
};

/** Whether the store has spoken to this cart at all, which is how this channel knows it may rely on it. */
export const storeHasNamedCartState = (cart: Cart | undefined): boolean => readCartCommitted(cart) !== 'unknown';

/** Whether this cart has been placed with the store, as far as anything here can say.
 */
export type OrderPlacement = 'placed' | 'not-placed' | 'unknown';

/**
 * Whether the guest's order has been placed. The store answers it where it has spoken; until then the
 * handshake this channel recorded when an order was opened for modification is the best it has, and a cart
 * with no handshake is reported as not placed because that is what every other page here already assumes.
 *
 * The store's answer always wins, including over a handshake that disagrees - a handshake is this browser's
 * memory of an intention, and the store's copy is what was actually done.
 * @param cart - The cart being drawn.
 * @param orderOpenForModify - Whether a handshake says an order is open for modification on this channel.
 * @returns What to tell the guest about their order.
 */
export const readOrderPlacement = (cart: Cart | undefined, orderOpenForModify: boolean): OrderPlacement => {
    switch (readCartCommitted(cart)) {
        case 'yes':
            return 'placed';
        case 'no':
            return 'not-placed';
        default:
            return orderOpenForModify ? 'placed' : 'not-placed';
    }
};

/**
 * Whether a cart may be thrown away by the guest. Only one that has never reached the store: an order the
 * store holds is the till's to void, and a cart whose state nobody has established is left alone.
 * @param placement - What is known about the order.
 * @param lines - How many lines the cart carries.
 * @returns True when offering to cancel is safe.
 */
export const canAbandonCart = (placement: OrderPlacement, lines: number): boolean => placement === 'not-placed' && lines > 0;

/**
 * The store's refusals of a change to a line. They are read rather than inferred: a control this channel
 * disabled can be bypassed by a caller that never drew it, so the refusal is the answer that counts.
 */
export type LineChangeRefusal = 'sent-to-kitchen' | 'price-set-at-store' | 'menu-unavailable';

const LINE_CHANGE_REFUSAL_CODES = new Map<string, LineChangeRefusal>([
    ['LSH_IntegrationLineAlreadySentToKitchen', 'sent-to-kitchen'],
    ['LSH_IntegrationLineMenuNoLongerAvailable', 'menu-unavailable'],
    ['LSH_IntegrationLinePriceSetAtStore', 'price-set-at-store']
]);

/** The refusal the store named, or undefined for anything this channel does not recognise as one. */
export const classifyLineChangeRefusal = (errorCode?: string): LineChangeRefusal | undefined =>
    errorCode ? LINE_CHANGE_REFUSAL_CODES.get(errorCode) : undefined;

/** Where a line stands for the guest: the four LAC cart line states, plus the till's void and the unestablished. */
export type LineStatus = 'pending' | 'sent-to-venue' | 'unavailable-not-sent' | 'unavailable-sent' | 'voided' | 'unknown';

/**
 * The line's state, from whether the store holds it and whether its menu is still on offer.
 * @param cart - The cart being drawn.
 * @param line - The line in question.
 * @param placement - Whether the order has been placed.
 * @param menuOnOffer - Whether the line's menu is among the active menus, or undefined where that is not known.
 * @returns The line's state.
 */
export const readLineStatus = (
    cart: Cart | undefined,
    line: CartLine | undefined,
    placement: OrderPlacement,
    menuOnOffer: boolean | undefined
): LineStatus => {
    const state = readLineOrderState(line);
    if (state.voidedAtTheTill === 'yes') {
        return 'voided';
    }

    const marked = readLineCommitment(cart, line);
    const commitment = marked === 'unknown' && placement === 'not-placed' ? 'no' : marked;
    if (commitment === 'unknown') {
        return 'unknown';
    }

    const menuEnded = state.menuAvailable === 'no' || menuOnOffer === false;
    if (commitment === 'yes') {
        return menuEnded ? 'unavailable-sent' : 'sent-to-venue';
    }

    return menuEnded ? 'unavailable-not-sent' : 'pending';
};

/** Whether the line's state leaves its quantity, note and content open to change. */
export const statusAllowsChange = (status: LineStatus): boolean => status !== 'unavailable-not-sent' && status !== 'unavailable-sent';

/** Whether the line's state leaves it open to removal. */
export const statusAllowsRemoval = (status: LineStatus): boolean => status !== 'unavailable-sent';

/** Whether the line has not reached the venue: the store does not hold it and the kitchen has not been told. */
export const isLineWithTheGuest = (status: LineStatus, state: ILineOrderState): boolean =>
    (status === 'pending' || status === 'unavailable-not-sent' || status === 'unknown') && state.sentToKitchen !== 'yes';

/** Whether the guest may take the line off: always where it has not reached the venue, otherwise as the store allows. */
export const guestMayRemoveLine = (status: LineStatus, state: ILineOrderState): boolean =>
    isLineWithTheGuest(status, state) || (mayRemoveLine(state) && state.sentToKitchen !== 'yes' && statusAllowsRemoval(status));

/** Whether checkout may go ahead: no unavailable line still unsent. */
export const canCheckOut = (statuses: LineStatus[]): boolean => !statuses.includes('unavailable-not-sent');

const STORE_LINE_FACTS = [
    LINE_COMMITTED_TO_ORDER,
    LINE_SENT_TO_KITCHEN,
    LINE_MENU_AVAILABLE,
    LINE_VOIDED_AT_THE_TILL,
    LINE_CAN_MODIFY,
    LINE_CAN_REMOVE,
    LINE_PRICE_SET_AT_STORE,
    LINE_STORE_PRICE,
    LINE_MENU_TYPE_TAKEN_FROM,
    LINE_KITCHEN_BLOCKED
];

/** A line's properties less what the store said about it, for a new line copied from one the store holds. */
export const withoutStoreLineFacts = (properties: CommerceProperty[] | undefined): CommerceProperty[] =>
    (properties ?? []).filter(property => !STORE_LINE_FACTS.includes(property.Key ?? ''));
